import { CommonModule, DatePipe, NgForOf, NgIf } from "@angular/common";
import { Component, inject, OnInit } from "@angular/core";
import { getHttpErrorMessage } from "../../../shared/utils/http-error.util";
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import {
  TuiAlertService,
  TuiAppearance,
  TuiButton,
  TuiDataList,
  TuiDropdown,
  TuiError,
  TuiIcon,
  TuiLink,
  TuiTextfield,
} from "@taiga-ui/core";
import {
  TuiBadge,
  TuiBadgedContent,
  TuiBadgeNotification,
  TuiBreadcrumbs,
  TuiButtonLoading,
  TuiDataListWrapper,
  TuiFade,
  TuiFieldErrorPipe,
  TuiItemsWithMore,
  TuiTabs,
  tuiValidationErrorsProvider,
} from "@taiga-ui/kit";
import {
  TuiCardLarge,
  TuiCell,
  TuiNavigation,
  TuiSearch,
} from "@taiga-ui/layout";
import { TuiTable } from "@taiga-ui/addon-table";
import { IAdmin } from "../../../model/interface/user";
import { catchError } from "rxjs/operators";
import { AdminService } from "../../../core/services/admin.service";
import { NotificationService } from "../../../core/services/notification.service";
import { RolesService } from "../../../core/services/roles.service";
import { AuthService } from "../../../core/services/auth.service";
import { TuiInputModule, TuiInputPasswordModule } from "@taiga-ui/legacy";
import { Breadcrumb } from "../../../model/interface/breadcrumb";
import { EnterSubmitDirective } from "../../../core/directives/enter-submit.directive";

@Component({
  selector: "app-user-profile",
  imports: [
    EnterSubmitDirective,
    FormsModule,
    NgForOf,
    NgIf,
    TuiAppearance,
    TuiDataListWrapper,
    TuiBreadcrumbs,
    TuiButton,
    TuiDataList,
    TuiDropdown,
    TuiFade,
    TuiTable,
    TuiLink,
    TuiNavigation,
    TuiTabs,
    TuiTextfield,
    TuiItemsWithMore,
    TuiSearch,
    ReactiveFormsModule,
    CommonModule,
    TuiBadge,
    TuiCardLarge,
    TuiButtonLoading,
    TuiBadgedContent,
    TuiFieldErrorPipe,
    TuiError,
    TuiInputModule,
    TuiInputPasswordModule,
  ],
  templateUrl: "./user-profile.component.html",
  styleUrl: "./user-profile.component.scss",
  providers: [
    DatePipe,
    tuiValidationErrorsProvider({
      required: "This field is required",
      minLength: "Password must be at least 8 characters",
      pattern:
        "Password must contain at least 8 characters, one uppercase letter, one lowercase letter, one number, and one special character",
      passwordMismatch: "Passwords do not match",
    }),
  ],
})
export class UserProfileComponent implements OnInit {
  breadcrumbs: Breadcrumb[] = [
    { label: "Royal Hospital", link: "/admin" },
    { label: "Users", link: "/admin/users" },
    { label: "User Ref No" }
  ];
  userId: string = "";
  user: IAdmin | null = null;
  isLoading: boolean = false;

  pageIndex: number = 1;
  pageSize: number = 10;
  total: number = 0;
  noMoreNotifications: boolean = false;
  notifications: any;
  unreadCount: number = 0;
  notificationLoading: boolean = false;
  changePasswordForm: any;
  isSuperAdmin: boolean = false;

  constructor(
    private userService: AdminService,
    private router: Router,
    private route: ActivatedRoute,
    private datePipe: DatePipe,
    private notificationService: NotificationService,
    private fb: FormBuilder,
    private rolesService: RolesService,
    private authService: AuthService
  ) {
    this.changePasswordForm = this.fb.group(
      {
        oldPassword: [null, [Validators.required]],
        password: [
          null,
          [
            Validators.required,
            Validators.pattern(
              /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/
            ),
          ],
        ],
        confirmPassword: [null, [Validators.required]],
      },
      {
        validator: this.passwordMatchValidator,
      }
    );
  }

  ngOnInit() {
    this.userId = this.route.snapshot.paramMap.get("id")!;
    
    // Check if viewing own profile and if user is superadmin
    try {
      const loggedInUser = this.authService.getLoggedInUser();
console.log("loggedInUser", loggedInUser.role);
      if (loggedInUser?._id === this.userId) {
        // If the logged-in user's role is already the role-name string 'SUPER ADMIN',
        // trust it and avoid extra API calls.
        const roleVal = loggedInUser?.role;
        if (typeof roleVal === 'string' && roleVal.toLowerCase() === 'super admin' ||
            (typeof roleVal === 'string' && roleVal.toLowerCase() === 'superadmin')) {
          this.isSuperAdmin = true;
        } else {
          // Only check superadmin status when viewing own profile and role not pre-known
          this.checkSuperAdmin();
        }
      }
    } catch (error) {
      console.error('Failed to get logged in user:', error);
    }
    
    if (this.userId) {
      this.loadUserData();
    }
  }
  
  checkSuperAdmin(): void {
    try {
      // If already determined, skip
      if (this.isSuperAdmin) return;

      const user = this.authService.getLoggedInUser();
      if (!user || !user.role) return;

      // Trust the role value directly - it's already the role name from the logged-in user
      const roleValue = user.role;
      
      if (typeof roleValue === 'string') {
        const roleName = roleValue.toLowerCase();
        this.isSuperAdmin = roleName === 'superadmin' || roleName === 'super admin';
      } else if (typeof roleValue === 'object' && roleValue.name) {
        this.isSuperAdmin = roleValue.name.toLowerCase() === 'superadmin' ||
                           roleValue.name.toLowerCase() === 'super admin';
      }
    } catch (error) {
      console.error('Failed to check super admin:', error);
    }
  }

  loadUserData(): void {
    this.isLoading = true;
    if (this.userId) {
      this.userService.getOneUserById(this.userId).subscribe((res: any) => {
        console.log("helllo",res);
        this.user = res;
        this.isLoading = false;
        if (this.user?.refNo) {
          this.breadcrumbs[2] = { label: this.user.refNo };
        }
        
        // If isSuperAdmin already set from ngOnInit (logged-in user check), skip role validation
        if (!this.isSuperAdmin && res?.role) {
          const roleValue = res.role;
          
          if (typeof roleValue === 'string') {
            const roleName = roleValue.toLowerCase();
            this.isSuperAdmin = roleName === 'superadmin' || roleName === 'super admin';
          } else if (typeof roleValue === 'object' && roleValue.name) {
            this.isSuperAdmin = roleValue.name.toLowerCase() === 'superadmin' ||
                               roleValue.name.toLowerCase() === 'super admin';
          }
        }
        
        this.getPagedNotifications();
      });
    }
  }

  navigate(link?: string): void {
    if (link) {
      this.router.navigate([link]);
    }
  }

  private readonly alerts = inject(TuiAlertService);

  protected activeItemIndex = 0;

  protected onClick(item: string): void {
    this.alerts.open(item).subscribe();
  }

  getPagedNotifications(): void {
    this.notificationLoading = true;
    const payload = {
      pageIndex: this.pageIndex,
      pageSize: this.pageSize,
      sortField: "createdAt",
      sortOrder: -1,
    };

    this.notificationService
      .getPagedNotificationsByUserId(this.userId, payload)
      .subscribe({
        next: (res: any) => {
          const page = Array.isArray(res) ? res[0] : null;
          this.notifications = Array.isArray(page?.data) ? page.data : [];
          if (this.notifications.length > 0) {
            this.total = page?.metadata?.[0]?.total ?? this.notifications.length;
            this.noMoreNotifications = this.notifications.length >= this.total;
          } else {
            this.total = 0;
            this.noMoreNotifications = true;
          }
          this.unreadCount = this.notifications.filter(
            (notification: any) => !notification.isOpened
          ).length;
          this.notificationLoading = false;
        },
        error: (error: any) => {
          console.error("Failed to get paginated data", error);
          this.notificationLoading = false;
        },
      });
  }

  markAllAsOpen(): void {
    this.notificationService
      .markAllNotificationsAsOpened(this.userId)
      .subscribe({
        next: () => {
          console.log(`Notifications marked as opened.`);
          this.getPagedNotifications();
        },
        error: (error) => {
          console.error(`Failed to mark all as opened:`, error);
        },
      });
  }

  toggleOpenedStatus(notificationId: string) {
    this.notificationLoading = true;
    this.notificationService
      .toggleNotificationOpenedStatus(notificationId)
      .subscribe({
        next: (response) => {
          this.notificationLoading = false;
          this.getPagedNotifications();
        },
        error: (error) => {
          this.notificationLoading = false;
          console.error("Failed to toggle notification status:", error);
        },
      });
  }

  loadMore(): void {
    if (!this.noMoreNotifications) {
      this.pageSize += 5;
      this.getPagedNotifications();
    }
  }

  passwordMatchValidator(
    formGroup: FormGroup
  ): { [key: string]: boolean } | null {
    if (
      formGroup.get("password")?.value !==
      formGroup.get("confirmPassword")?.value
    ) {
      return { passwordMismatch: true };
    }
    return null;
  }

  submitChangePassword(): void {
    if (this.changePasswordForm.valid && this.userId) {
      this.isLoading = true;
      const payload = {
        id: this.userId,
        oldPassword: this.changePasswordForm.value.oldPassword,
        password: this.changePasswordForm.value.password,
      };

      this.userService
        .changePassword(payload)
        .pipe(
          catchError((error) => {
            this.isLoading = false;
            this.changePasswordForm.setErrors({ invalid: true });
            this.alerts
              .open(getHttpErrorMessage(error), {
                label: "Error",
                appearance: "negative",
              })
              .subscribe();
            this.changePasswordForm.reset();
            return [];
          })
        )
        .subscribe((response: any) => {
          this.isLoading = false;
          if (response?.oldPwdCheck) {
            this.alerts
              .open(response.message, {
                label: "Error",
                appearance: "negative",
              })
              .subscribe();
          } else {
            this.alerts
              .open("Password changed successfully.", {
                label: "Success",
                appearance: "success",
              })
              .subscribe();
            this.changePasswordForm.reset();
          }
        });
    }else if (this.changePasswordForm.hasError('passwordMismatch')) { 
      this.alerts
        .open("Passwords do not match.", {
          label: "Error",
          appearance: "negative",
        })
        .subscribe();
    }
    else {
      this.changePasswordForm.markAllAsTouched();
      this.alerts
        .open("Form is invalid.", {
          label: "Error",
          appearance: "negative",
        })
        .subscribe();
    }
  }
}
